import { useStudentApplicationForm, useStudentEndSurvey } from "api/hooks";
import React, { useState } from "react";

import { formatDate } from "utils";
import { PaziaLoader, PaziaButton, Stars } from "components/atoms";
import { COURSE_FORM_FIELD_TYPES__DATES_TYPES } from "consts";
import { COURSE_FORM_FIELD_TYPES } from "consts";
import { EditRegistrationModal } from "../../Registrations/EditRegistrationModal";
import { RegistrationApi } from "api/registrations";
import { EditRegistrationBuyerFieldsModal } from "./EditRegistrationBuyerFieldsModal";

interface StudentApplicationFormProps {
  studentId: number;
  courseId: number;
}

interface CourseRatingSectionProps {
  courseId: number;
  studentId: number;
}

const CourseRatingSection = ({ courseId, studentId }: CourseRatingSectionProps) => {
  const { isLoading, courseRating, isError } = useStudentEndSurvey(courseId, studentId);

  if (isLoading) {
    return (
      <div className="rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-3 sm:p-4 dark:border-amber-800 dark:from-amber-900/20 dark:to-yellow-900/20">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 sm:h-10 sm:w-10 dark:bg-amber-900/50">
            <svg
              className="h-4 w-4 text-amber-600 sm:h-5 sm:w-5 dark:text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="h-4 w-24 animate-pulse rounded bg-amber-200 dark:bg-amber-700"></div>
          </div>
        </div>
      </div>
    );
  }

  // No survey data (404 or error) - student hasn't filled the survey
  if (isError || !courseRating) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4 dark:border-slate-700 dark:bg-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 sm:h-10 sm:w-10 dark:bg-slate-700">
            <svg
              className="h-4 w-4 text-slate-500 sm:h-5 sm:w-5 dark:text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-tight text-slate-600 sm:text-lg dark:text-slate-400">
              Ocena kursu
            </h3>
            <p className="text-xs leading-tight text-slate-500 sm:text-sm dark:text-slate-500">
              Student nie wypelnit ankiety koncowej kursu
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Has course rating - display stars and comment
  return (
    <div className="rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 p-3 sm:p-4 dark:border-amber-800 dark:from-amber-900/20 dark:to-yellow-900/20">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 sm:h-10 sm:w-10 dark:bg-amber-900/50">
          <svg
            className="h-4 w-4 text-amber-600 sm:h-5 sm:w-5 dark:text-amber-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold leading-tight text-slate-900 sm:text-lg dark:text-slate-100">
            Ocena kursu
          </h3>
          <div className="mt-1 flex items-center gap-2">
            <Stars numberOfStars={courseRating.rating} />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">({courseRating.rating}/5)</span>
          </div>
        </div>
        <div className="flex-shrink-0 text-xs text-slate-500 dark:text-slate-400">
          {formatDate(courseRating.createdDate, true, true)}
        </div>
      </div>

      {/* Comment section */}
      {courseRating.comment && (
        <div className="mt-3 rounded-lg border border-amber-100 bg-white/50 p-3 dark:border-amber-900/50 dark:bg-slate-800/50">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Komentarz</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700 dark:text-slate-300">{courseRating.comment}</p>
        </div>
      )}
    </div>
  );
};

export const StudentApplicationForm = ({ studentId, courseId }: StudentApplicationFormProps) => {
  const { loading, data, refetch } = useStudentApplicationForm(studentId, courseId);
  const [editingRegistration, setEditingRegistration] = useState<RegistrationApi | null>(null);
  const [editingBuyerFields, setEditingBuyerFields] = useState(false);

  // Create a minimal registration object for the EditRegistrationModal
  const createRegistrationForEdit = (): RegistrationApi | null => {
    if (!data) return null;

    return {
      id: data.registrationId,
      userId: studentId,
      courseId: courseId,
      courseName: "", // Not needed for the edit functionality
      studentFirstName: "", // Not needed for the edit functionality
      studentSurname: "",
      studentEmail: "",
      fullyPaid: false,
      isInvoice: false,
      createdDate: data.createdDate,
      updatedDate: undefined,
    };
  };

  const handleEditClick = () => {
    const registration = createRegistrationForEdit();
    if (registration) {
      setEditingRegistration(registration);
    }
  };

  if (loading)
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
        <PaziaLoader position="center" />
      </div>
    );

  if (!data)
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50">
        <div className="text-center text-slate-500 dark:text-slate-400">
          <p className="text-sm">Brak danych formularza aplikacyjnego</p>
        </div>
      </div>
    );

  const resolveValue = (value: any, fieldType: number): string | React.ReactElement => {
    // Convert value to string if it's not already
    const stringValue = value != null ? String(value) : "";

    // Handle empty or undefined values
    if (!value || (typeof value === "string" && value.trim() === "") || stringValue.trim() === "") {
      return (
        <span className="inline-flex items-center rounded bg-slate-100 px-2 py-1 text-xs text-slate-400 dark:bg-slate-700 dark:text-slate-500">
          Brak danych
        </span>
      );
    }

    if (COURSE_FORM_FIELD_TYPES__DATES_TYPES.includes(fieldType)) {
      try {
        return (
          <span className="inline-flex items-center rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            {formatDate(new Date(stringValue))}
          </span>
        );
      } catch {
        return (
          <span className="inline-flex items-center rounded bg-slate-100 px-2 py-1 text-xs text-slate-400 dark:bg-slate-700 dark:text-slate-500">
            Nieprawidłowa data
          </span>
        );
      }
    }

    if (fieldType === COURSE_FORM_FIELD_TYPES.BOOLEAN) {
      return stringValue === "true" || stringValue === "1" || stringValue === "yes" ? (
        <span className="inline-flex items-center rounded bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          TAK
        </span>
      ) : (
        <span className="inline-flex items-center rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
          NIE
        </span>
      );
    }

    // Handle long text values
    if (typeof value === "string" && value.length > 50) {
      return <div className="text-sm leading-relaxed text-slate-900 dark:text-slate-100">{value}</div>;
    }

    return <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{stringValue}</span>;
  };

  return (
    <div className="mt-4 space-y-6">
      <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 dark:border-blue-800 dark:from-blue-900/20 dark:to-indigo-900/20">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:h-10 sm:w-10 dark:bg-blue-900/50">
            <svg
              className="h-4 w-4 text-blue-600 sm:h-5 sm:w-5 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-tight text-slate-900 sm:text-lg dark:text-slate-100">
              Formularz aplikacyjny
            </h3>
            <p className="text-xs leading-tight text-slate-600 sm:text-sm dark:text-slate-400">
              Wypełniono: {formatDate(data.createdDate, true, true)}
            </p>
          </div>
          <div className="flex-shrink-0">
            <PaziaButton type="secondary" title="Edytuj" onClick={handleEditClick} />
          </div>
        </div>
      </div>

      {/* Form Groups */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
        {data.groups?.length > 0 ? (
          data.groups.map((group) => (
            <div
              key={group.name}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
            >
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-3 sm:px-4 dark:border-slate-700 dark:bg-slate-800">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-900 sm:text-sm dark:text-slate-100">
                  {group.name}
                </h4>
                {group.description && (
                  <p className="mt-0.5 text-xs italic text-slate-500 dark:text-slate-400">{group.description}</p>
                )}
              </div>
              <div className="space-y-3 p-3 sm:p-4">
                {group.fields?.length > 0 ? (
                  group.fields.map((field, fieldIndex) => (
                    <div
                      key={field.name}
                      className="flex flex-col gap-2 border-b border-slate-100 py-2 last:border-b-0 sm:flex-row sm:items-start sm:justify-between dark:border-slate-800"
                    >
                      <div className="sm:w-1/2 sm:pr-2">
                        <label className="text-xs font-medium leading-tight text-slate-700 sm:text-sm dark:text-slate-300">
                          {field.name}
                        </label>
                      </div>
                      <div className="sm:w-1/2 sm:text-right">
                        <div className="break-words text-xs text-slate-900 sm:text-sm dark:text-slate-100">
                          {resolveValue(field.value, field.type)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-4 text-center text-slate-500 dark:text-slate-400">
                    <p className="text-sm">Brak pól w tej sekcji</p>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-900">
            <p className="text-slate-500 dark:text-slate-400">Brak grup do wyświetlenia</p>
          </div>
        )}
      </div>

      {/* Course Rating */}
      <CourseRatingSection courseId={courseId} studentId={studentId} />

      {/* Dane nabywcy (rejestracja) */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            Dane nabywcy (rejestracja)
          </h3>
          <PaziaButton
            btnType="button"
            title="Edytuj"
            type="tertiary"
            onClick={() => setEditingBuyerFields(true)}
          />
        </div>
        <div className="space-y-1 text-sm text-slate-700 dark:text-slate-300">
          <div>
            <span className="font-medium">Faktura: </span>
            {data.isInvoice ? (
              <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">Tak</span>
            ) : (
              <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">Nie</span>
            )}
          </div>
          {data.buyer && <div><span className="font-medium">Nabywca: </span>{data.buyer}</div>}
          {data.buyerNIP && <div><span className="font-medium">NIP: </span>{data.buyerNIP}</div>}
          {data.buyerAddress && <div><span className="font-medium">Adres: </span>{data.buyerAddress}</div>}
          {!data.isInvoice && !data.buyer && !data.buyerNIP && !data.buyerAddress && (
            <p className="text-xs text-slate-400 dark:text-slate-500">Brak danych nabywcy</p>
          )}
        </div>
      </div>

      {editingBuyerFields && (
        <EditRegistrationBuyerFieldsModal
          studentId={studentId}
          courseId={courseId}
          initialValues={{
            isInvoice: data.isInvoice,
            buyer: data.buyer,
            buyerNIP: data.buyerNIP,
            buyerAddress: data.buyerAddress,
          }}
          onClose={() => setEditingBuyerFields(false)}
          onSuccess={() => {
            setEditingBuyerFields(false);
            refetch();
          }}
        />
      )}

      <EditRegistrationModal
        registration={editingRegistration}
        onCloseRequest={() => setEditingRegistration(null)}
        onSuccess={() => {
          setEditingRegistration(null);
          refetch();
        }}
      />
    </div>
  );
};
